import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Container,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Paper,
  Grid,
  Chip,
  Button,
  Divider,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField
} from '@mui/material';
import {
  Business,
  LocationOn,
  Phone,
  Email,
  ArrowBack,
  LocalShipping,
  CheckCircle,
  Star,
  ShoppingCart,
  Message,
  AccessTime
} from '@mui/icons-material';
import MerchantDirectory from '../components/merchants/MerchantDirectory';
import QuoteRequestWizard from '../components/quotes/QuoteRequestWizard';
import api from '../services/api';
import DialogCloseButton from '../components/common/DialogCloseButton';

const MerchantsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [merchant, setMerchant] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [quoteWizardOpen, setQuoteWizardOpen] = useState(false);
  const [projectSelectDialogOpen, setProjectSelectDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [catalogItems, setCatalogItems] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogTotal, setCatalogTotal] = useState(0);
  const [catalogCategories, setCatalogCategories] = useState([]);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogCategory, setCatalogCategory] = useState('');
  const [pickupCart, setPickupCart] = useState({}); // { [itemId]: { item, quantity } }
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [orderProjectId, setOrderProjectId] = useState('');
  const [walletBalance, setWalletBalance] = useState(null);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [orderResult, setOrderResult] = useState(null); // success payload incl. pickup_code
  const [toppingUp, setToppingUp] = useState(false);
  const [topUpError, setTopUpError] = useState('');

  useEffect(() => {
    if (id) {
      loadMerchantProfile(id);
    }
  }, [id]);

  // Check if quote request was triggered from URL
  useEffect(() => {
    const requestQuote = searchParams.get('requestQuote');
    if (requestQuote === 'true' && merchant && user?.role === 'client') {
      // Remove the query parameter from URL
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('requestQuote');
      setSearchParams(newSearchParams, { replace: true });
      // Trigger quote request after a short delay to ensure state is ready
      setTimeout(() => {
        handleRequestQuote();
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchant, user]);

  useEffect(() => {
    if (projectSelectDialogOpen && user?.role === 'client') {
      loadProjects();
    }
  }, [projectSelectDialogOpen, user]);

  const loadMerchantProfile = async (merchantId) => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get(`/merchants/${merchantId}`);
      
      if (response.data.success) {
        setMerchant(response.data.data);
        loadCatalog(merchantId);
      } else {
        setError('Merchant profile not found');
      }
    } catch (err) {
      console.error('Error loading merchant profile:', err);
      setError(err.response?.data?.message || 'Failed to load merchant profile');
    } finally {
      setLoading(false);
    }
  };

  const loadProjects = async () => {
    try {
      setLoadingProjects(true);
      const response = await api.get('/projects');
      
      if (response.data.success && response.data.data?.projects) {
        const projectsList = response.data.data.projects;
        setProjects(projectsList);
        // Pre-select first project if available
        if (projectsList.length > 0 && !selectedProject) {
          setSelectedProject(projectsList[0]);
        }
      }
    } catch (err) {
      console.error('Error loading projects:', err);
    } finally {
      setLoadingProjects(false);
    }
  };

  const CATALOG_PAGE = 30;
  const loadCatalog = async (merchantProfileId, { append = false, search = catalogSearch, category = catalogCategory } = {}) => {
    try {
      setCatalogLoading(true);
      const response = await api.get(`/merchants/${merchantProfileId}/catalog`, {
        params: {
          limit: CATALOG_PAGE,
          offset: append ? catalogItems.length : 0,
          search: search.trim() || undefined,
          category: category || undefined,
        },
      });
      if (response.data.success) {
        const page = response.data.data || [];
        setCatalogItems((prev) => (append ? [...prev, ...page] : page));
        setCatalogTotal(response.data.pagination?.total ?? page.length);
        if (response.data.categories) setCatalogCategories(response.data.categories);
      }
    } catch (err) {
      console.error('Error loading catalog:', err);
      if (!append) setCatalogItems([]);
    } finally {
      setCatalogLoading(false);
    }
  };

  // Suppliers with big price lists need search, not scrolling.
  useEffect(() => {
    if (!merchant?.id) return undefined;
    const t = setTimeout(() => loadCatalog(merchant.id), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogSearch, catalogCategory]);

  const cartTotal = () =>
    Object.values(pickupCart).reduce((sum, entry) => sum + entry.quantity * parseFloat(entry.item.unit_price), 0);

  const closeOrderDialog = () => {
    setOrderDialogOpen(false);
    if (orderResult) setPickupCart({});
    setOrderResult(null);
    setOrderProjectId('');
    setWalletBalance(null);
    setToppingUp(false);
    setTopUpError('');
  };

  const handleTopUpWallet = async () => {
    try {
      setToppingUp(true);
      setTopUpError('');

      const ensureResponse = await api.post('/material-orders/escrow/ensure', { projectId: orderProjectId });
      const escrow = ensureResponse.data?.data;
      if (!ensureResponse.data?.success || !escrow?.id) {
        throw new Error('Failed to set up project wallet.');
      }

      const shortfall = Math.max(Math.ceil((cartTotal() - (walletBalance || 0)) * 100) / 100, 0.50);
      const baseUrl = `${window.location.origin}${window.location.pathname}`;

      const checkoutResponse = await api.post('/stripe/create-checkout-session', {
        escrowId: escrow.id,
        amount: shortfall,
        currency: 'USD',
        paymentMethodTypes: ['card'],
        successUrl: `${baseUrl}?wallet_funded=true`,
        cancelUrl: `${baseUrl}?wallet_cancelled=true`
      });

      const checkoutUrl = checkoutResponse.data?.data?.checkoutUrl || checkoutResponse.data?.checkoutUrl;
      if (!checkoutUrl) {
        throw new Error('Failed to get checkout URL.');
      }
      window.location.href = checkoutUrl;
    } catch (err) {
      console.error('Error initiating wallet top-up:', err);
      setTopUpError(err.response?.data?.error || err.message || 'Failed to initiate payment');
    } finally {
      setToppingUp(false);
    }
  };

  const placeDirectOrder = async () => {
    try {
      setPlacingOrder(true);
      const response = await api.post('/material-orders/direct', {
        projectId: orderProjectId,
        merchantProfileId: merchant.id,
        items: Object.values(pickupCart).map((entry) => ({ catalogItemId: entry.item.id, quantity: entry.quantity }))
      });
      if (response.data.success) {
        setOrderResult(response.data.data);
        loadCatalog(merchant.id);
      } else {
        setError(response.data.error || 'Failed to place order.');
      }
    } catch (err) {
      console.error('Error placing pickup order:', err);
      setError(err.response?.data?.error || 'Failed to place order.');
    } finally {
      setPlacingOrder(false);
    }
  };

  const handleRequestQuote = async () => {
    if (!user || user.role !== 'client') {
      setError('You must be logged in as a client to request quotes');
      return;
    }
    
    // Open project selection dialog first
    setProjectSelectDialogOpen(true);
  };

  const handleProjectSelected = () => {
    if (!selectedProject) {
      setError('Please select a project');
      return;
    }
    
    // Close project selection dialog and open quote wizard
    setProjectSelectDialogOpen(false);
    setQuoteWizardOpen(true);
  };

  const handleProjectSelectCancel = () => {
    setProjectSelectDialogOpen(false);
    setSelectedProject(null);
  };

  // If viewing a specific merchant profile, show that
  if (id) {
    if (loading) {
      return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
            <CircularProgress />
          </Box>
        </Container>
      );
    }

    if (error) {
      return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/suppliers')}>
            Back to Suppliers Directory
          </Button>
        </Container>
      );
    }

    if (!merchant) {
      return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Alert severity="info">
            Merchant profile not found.
          </Alert>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/suppliers')} sx={{ mt: 2 }}>
            Back to Suppliers Directory
          </Button>
        </Container>
      );
    }

    const physicalAddress = merchant.physical_address || {};
    const deliveryOptions = merchant.delivery_options || {};
    const specializations = merchant.specializations || [];
    const serviceAreas = merchant.service_areas || [];

    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/suppliers')}
          sx={{ mb: 3 }}
        >
          Back to Suppliers Directory
        </Button>

        <Paper sx={{ p: 4, mb: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Avatar sx={{ width: 80, height: 80, bgcolor: 'primary.main' }}>
                  <Business sx={{ fontSize: 40 }} />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {merchant.business_name || merchant.trading_name || 'Merchant'}
                    {merchant.verified && (
                      <CheckCircle color="primary" sx={{ ml: 1, verticalAlign: 'middle' }} />
                    )}
                  </Typography>
                  {merchant.trading_name && merchant.trading_name !== merchant.business_name && (
                    <Typography variant="body2" color="text.secondary">
                      Trading as: {merchant.trading_name}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <LocationOn fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {physicalAddress.city || merchant.city || 'Location not specified'}
                      {physicalAddress.province && `, ${physicalAddress.province}`}
                    </Typography>
                  </Box>
                  </Box>
              </Box>

              {/* Performance Metrics */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2, flexWrap: 'wrap' }}>
                {merchant.rating_average && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Star sx={{ fontSize: 20, color: '#ffc107' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {parseFloat(merchant.rating_average).toFixed(1)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ({merchant.total_ratings || 0} ratings)
                    </Typography>
                  </Box>
                )}
                {merchant.total_orders_completed !== undefined && (
                  <Typography variant="body2" color="text.secondary">
                    Orders Completed: <strong>{merchant.total_orders_completed || 0}</strong>
                  </Typography>
                )}
                {merchant.on_time_delivery_rate !== undefined && (
                  <Typography variant="body2" color="text.secondary">
                    On-Time Rate: <strong>{merchant.on_time_delivery_rate || 0}%</strong>
                  </Typography>
                )}
                {merchant.quote_response_time_avg && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AccessTime sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      Avg Response: <strong>{merchant.quote_response_time_avg} hours</strong>
                    </Typography>
                  </Box>
                )}
              </Box>

              {merchant.business_description && (
                <Typography variant="body1" paragraph>
                  {merchant.business_description}
                </Typography>
              )}

              {specializations.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Specializations:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {specializations.map((spec, index) => (
                      <Chip key={index} label={spec} size="small" />
                    ))}
                  </Box>
                </Box>
              )}
            </Grid>

            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Contact & Details
                  </Typography>
                  <List dense>
                    {merchant.business_email && (
                      <ListItem>
                        <ListItemIcon>
                          <Email fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={merchant.business_email} />
                      </ListItem>
                    )}
                    {merchant.business_phone && (
                      <ListItem>
                        <ListItemIcon>
                          <Phone fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={merchant.business_phone} />
                      </ListItem>
                    )}
                    {merchant.minimum_order_value > 0 && (
                      <ListItem>
                        <ListItemIcon>
                          <ShoppingCart fontSize="small" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={`Minimum Order: $${merchant.minimum_order_value.toFixed(2)}`}
                        />
                      </ListItem>
                    )}
                  </List>
                  <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={<ShoppingCart />}
                      onClick={handleRequestQuote}
                      disabled={!user || user.role !== 'client'}
                    >
                      Request Quote
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>

        <Grid container spacing={3}>
          {physicalAddress.street && (
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  <LocationOn sx={{ verticalAlign: 'middle', mr: 1 }} />
                  Address
                </Typography>
                <Typography variant="body2">
                  {physicalAddress.street}
                  {physicalAddress.suburb && `, ${physicalAddress.suburb}`}
                  <br />
                  {physicalAddress.city}
                  {physicalAddress.province && `, ${physicalAddress.province}`}
                  {physicalAddress.postal_code && ` ${physicalAddress.postal_code}`}
                </Typography>
              </Paper>
            </Grid>
          )}

          {deliveryOptions.offers_delivery && (
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  <LocalShipping sx={{ verticalAlign: 'middle', mr: 1 }} />
                  Delivery Options
                </Typography>
                <Typography variant="body2" paragraph>
                  {deliveryOptions.delivery_fee_structure || 'Delivery available'}
                </Typography>
                {deliveryOptions.delivery_radius_km > 0 && (
                  <Typography variant="body2">
                    Delivery radius: {deliveryOptions.delivery_radius_km} km
                  </Typography>
                )}
                {deliveryOptions.delivery_time_estimate && (
                  <Typography variant="body2">
                    Estimated delivery time: {deliveryOptions.delivery_time_estimate}
                  </Typography>
                )}
              </Paper>
            </Grid>
          )}

          {serviceAreas.length > 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Service Areas
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {serviceAreas.map((area, index) => (
                    <Chip key={index} label={area} size="small" variant="outlined" />
                  ))}
                </Box>
              </Paper>
            </Grid>
          )}

          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                <ShoppingCart sx={{ verticalAlign: 'middle', mr: 1 }} />
                Products
              </Typography>
              {(catalogTotal > CATALOG_PAGE || catalogSearch || catalogCategory || catalogCategories.length > 1) && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                  <TextField size="small" placeholder="Search products or codes…" value={catalogSearch}
                    onChange={(e) => setCatalogSearch(e.target.value)} sx={{ minWidth: 240, flex: 1, maxWidth: 360 }} />
                  {catalogCategories.length > 1 && (
                    <TextField select size="small" label="Category" value={catalogCategory}
                      onChange={(e) => setCatalogCategory(e.target.value)} sx={{ minWidth: 200 }}>
                      <MenuItem value="">All categories</MenuItem>
                      {catalogCategories.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                    </TextField>
                  )}
                </Box>
              )}
              {catalogLoading && catalogItems.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : catalogItems.length === 0 ? (
                <Typography color="text.secondary">
                  {catalogSearch || catalogCategory
                    ? 'No products match that search.'
                    : 'This supplier has not published any products yet.'}
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  {catalogItems.map((item) => (
                    <Grid item xs={12} sm={6} md={4} key={item.id}>
                      <Card variant="outlined" sx={{ p: 2, height: '100%' }}>
                        <Typography sx={{ fontWeight: 600 }}>{item.name}</Typography>
                        {item.category && (
                          <Chip size="small" label={item.category} sx={{ mt: 0.5 }} />
                        )}
                        {item.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {item.description}
                          </Typography>
                        )}
                        <Typography variant="h6" sx={{ mt: 1 }}>
                          ${parseFloat(item.unit_price).toFixed(2)}{' '}
                          <Typography component="span" variant="body2" color="text.secondary">
                            / {item.unit}
                          </Typography>
                        </Typography>
                        {item.available_quantity !== undefined && item.available_quantity !== null && (
                          <Typography variant="caption" color="text.secondary">
                            {item.available_quantity} {item.unit} available
                          </Typography>
                        )}
                        {user?.role === 'client' && (
                          <Box sx={{ display: 'flex', gap: 1, mt: 1.5, alignItems: 'center' }}>
                            <TextField
                              size="small"
                              type="number"
                              label="Qty"
                              sx={{ width: 100 }}
                              inputProps={{ min: 1 }}
                              value={pickupCart[item.id]?.quantity || ''}
                              onChange={(e) => {
                                const quantity = parseFloat(e.target.value);
                                setPickupCart((prev) => {
                                  const next = { ...prev };
                                  if (!quantity || quantity <= 0) {
                                    delete next[item.id];
                                  } else {
                                    next[item.id] = { item, quantity };
                                  }
                                  return next;
                                });
                              }}
                            />
                          </Box>
                        )}
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
              {catalogItems.length > 0 && catalogItems.length < catalogTotal && (
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Button variant="outlined" disabled={catalogLoading} onClick={() => loadCatalog(merchant.id, { append: true })}>
                    {catalogLoading ? 'Loading…' : `Show more (${catalogTotal - catalogItems.length} more)`}
                  </Button>
                </Box>
              )}

              {user?.role === 'client' && Object.keys(pickupCart).length > 0 && (
                <Paper
                  variant="outlined"
                  sx={{ p: 2, mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}
                >
                  <Typography>
                    {Object.keys(pickupCart).length} item(s) — total ${cartTotal().toFixed(2)}
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => { setOrderResult(null); setOrderDialogOpen(true); loadProjects(); }}
                  >
                    Order for Pickup
                  </Button>
                </Paper>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Project Selection Dialog */}
        <Dialog
          open={projectSelectDialogOpen}
          onClose={handleProjectSelectCancel}
          maxWidth="sm"
          fullWidth
        >
          <DialogCloseButton onClick={handleProjectSelectCancel} />
          <DialogTitle>
            Select Project for Quote Request
          </DialogTitle>
          <DialogContent>
            {loadingProjects ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : projects.length === 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                You need to create a project first before requesting quotes.
              </Alert>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Select the project for which you want to request a quote from {merchant?.business_name || merchant?.trading_name}:
                </Typography>
                <FormControl fullWidth>
                  <InputLabel>Select Project *</InputLabel>
                  <Select
                    value={selectedProject?.id || ''}
                    label="Select Project *"
                    onChange={(e) => {
                      const project = projects.find(p => p.id === e.target.value);
                      setSelectedProject(project);
                    }}
                  >
                    {projects.map((project) => (
                      <MenuItem key={project.id} value={project.id}>
                        {project.title || project.name || `Project ${project.id}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleProjectSelectCancel}>
              Cancel
            </Button>
            <Button
              onClick={handleProjectSelected}
              variant="contained"
              disabled={!selectedProject || loadingProjects || projects.length === 0}
            >
              Continue
            </Button>
          </DialogActions>
        </Dialog>

        {/* Quote Request Wizard */}
        {selectedProject && (
          <QuoteRequestWizard
            open={quoteWizardOpen}
            onClose={(success) => {
              setQuoteWizardOpen(false);
              setSelectedProject(null);
              if (success) {
                // Optionally reload or show success message
                navigate('/quotes');
              }
            }}
            project={selectedProject}
            preSelectedMerchantId={merchant?.user_id || merchant?.id}
          />
        )}

        {/* Pickup Order Dialog */}
        <Dialog
          open={orderDialogOpen}
          onClose={closeOrderDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogCloseButton onClick={closeOrderDialog} />
          <DialogTitle>Order for Pickup</DialogTitle>
          <DialogContent>
            {orderResult ? (
              <Alert severity="success" sx={{ mt: 1 }}>
                Order placed. Your pickup code is <strong>{orderResult.pickup_code}</strong> — share
                it only with the person collecting the order; the merchant will not see this code.
                You can view it again any time on the order page.
              </Alert>
            ) : (
              <>
                <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
                  <InputLabel>Project *</InputLabel>
                  <Select
                    value={orderProjectId}
                    label="Project *"
                    onChange={async (e) => {
                      const projectId = e.target.value;
                      setOrderProjectId(projectId);
                      setWalletBalance(null);
                      try {
                        const res = await api.get(`/escrow/project/${projectId}`);
                        const esc = res.data?.data;
                        const balance = esc
                          ? parseFloat(esc.available_balance ?? (esc.total_amount - (esc.held_amount || 0)))
                          : 0;
                        setWalletBalance(balance);
                      } catch {
                        setWalletBalance(0);
                      }
                    }}
                  >
                    {projects.map((project) => (
                      <MenuItem key={project.id} value={project.id}>
                        {project.title || project.name || `Project ${project.id}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {Object.values(pickupCart).map((entry) => (
                  <Box key={entry.item.id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2">
                      {entry.item.name} × {entry.quantity}
                    </Typography>
                    <Typography variant="body2">
                      ${(entry.quantity * parseFloat(entry.item.unit_price)).toFixed(2)}
                    </Typography>
                  </Box>
                ))}
                <Divider sx={{ my: 1.5 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontWeight: 600 }}>Total</Typography>
                  <Typography sx={{ fontWeight: 600 }}>${cartTotal().toFixed(2)}</Typography>
                </Box>

                {walletBalance !== null && (
                  <>
                    <Alert severity={walletBalance >= cartTotal() ? 'info' : 'warning'} sx={{ mt: 2 }}>
                      Wallet balance: ${walletBalance.toFixed(2)}
                      {walletBalance < cartTotal() && ' — insufficient funds for this order.'}
                    </Alert>
                    {walletBalance < cartTotal() && (
                      <Box sx={{ mt: 1.5 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleTopUpWallet}
                          disabled={toppingUp || !orderProjectId}
                        >
                          {toppingUp ? 'Redirecting…' : 'Top Up Wallet'}
                        </Button>
                      </Box>
                    )}
                    {topUpError && (
                      <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setTopUpError('')}>
                        {topUpError}
                      </Alert>
                    )}
                  </>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeOrderDialog}>
              {orderResult ? 'Done' : 'Cancel'}
            </Button>
            {!orderResult && (
              <Button
                variant="contained"
                disabled={!orderProjectId || placingOrder || (walletBalance !== null && walletBalance < cartTotal())}
                onClick={placeDirectOrder}
              >
                {placingOrder ? 'Placing…' : 'Place Order'}
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Container>
    );
  }
  
  // Show directory
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          Find Suppliers
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Browse verified suppliers for your material needs. Filter by location, specialization, and ratings.
        </Typography>
      </Box>
      <MerchantDirectory />
    </Container>
  );
};

export default MerchantsPage;

